import { ClerkLoaded, useAuth } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import AcceptOrgInvitation from '@/pages/auth/AcceptOrgInvitation';
import { ConfirmEmail } from '@/pages/auth/ConfirmEmail';
import { ForgetPassword } from '@/pages/auth/ForgetPassword';
import { JoinOrg } from '@/pages/auth/JoinOrg';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { SignIn } from '@/pages/auth/SignIn';
import { SignUp } from '@/pages/auth/SignUp';
import { SSOCallback } from '@/pages/auth/SSOCallback';

const isInternalSubDomain = (w: Window & typeof globalThis) => {
  const hostname = w?.location?.hostname;
  const subDomain = hostname?.split('.')?.[0];
  if (!subDomain) return true;
  const internalSubDomains = [
    'localhost',
    'zees-dev',
    'zees',
    'consultants-dev',
    'consultants',
  ];
  return internalSubDomains.includes(subDomain);
};

export const AuthRoutes = () => {
  const location = useLocation();
  const { orgId } = useParams();
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (orgId) {
      window.localStorage.setItem('active-org-id', orgId);
    }
  }, [orgId]);

  if (!isLoaded) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  const activeOrgId = window.localStorage.getItem('active-org-id');
  const isConfirmPath = location.pathname === `/${activeOrgId}/auth/confirm`;
  const isJoinOrgPath = location.pathname === `/${activeOrgId}/auth/join-org`;
  const isSSOCallbackPath =
    location.pathname === `/${activeOrgId}/auth/sso-callback`;

  const isInternal = isInternalSubDomain(window);

  if (isSignedIn && !isConfirmPath && !isJoinOrgPath && !isSSOCallbackPath) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthLayout>
      <Routes>
        <Route
          path="/sign-in"
          element={<SignIn isInternalHostname={isInternal} />}
        />
        <Route
          path="/sign-up"
          element={<SignUp isInternalHostname={isInternal} />}
        />
        <Route
          path="/confirm"
          element={
            <ClerkLoaded>
              <ConfirmEmail />
            </ClerkLoaded>
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword isInternalHostname={isInternal} />}
        />
        <Route
          path="/forget-password"
          element={<ForgetPassword isInternalHostname={isInternal} />}
        />
        {/* callback routes */}
        <Route path="/join-org" element={<JoinOrg />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route
          path="/accept-org-invitation"
          element={<AcceptOrgInvitation isInternalHostname={isInternal} />}
        />
      </Routes>
    </AuthLayout>
  );
};
